import { useNavigate } from 'react-router-dom';
import {
  User,
  ShieldCheck,
  Bell,
  HelpCircle,
  ChevronRight,
  Share2,
  Settings,
  Flame,
  LogOut,
  Award,
  type LucideIcon,
} from 'lucide-react';
import type { CmsBlock } from './BlockRenderer';

// ─── Mock data ────────────────────────────────────────────────────────────────

const MOCK_USER = {
  name: 'Marco Rossi',
  level: 12,
  streak: 5,
  points: 12450,
  badgesUnlocked: 2,
  badgesTotal: 6,
  memberId: '#LOTTO-u1-982',
  memberType: 'Premium Club Member',
};

// ─── Settings item ────────────────────────────────────────────────────────────

interface SettingItem {
  icon: LucideIcon;
  label: string;
  subtitle: string;
  iconBg: string;
}

const SETTINGS: SettingItem[] = [
  {
    icon: User,
    label: 'Dati Personali',
    subtitle: 'Gestisci le tue informazioni di base',
    iconBg: 'rgba(43,127,255,0.1)',
  },
  {
    icon: ShieldCheck,
    label: 'Sicurezza e Privacy',
    subtitle: 'Password, 2FA e consensi',
    iconBg: 'rgba(173,70,255,0.1)',
  },
  {
    icon: Bell,
    label: 'Notifiche',
    subtitle: 'Scegli cosa ricevere',
    iconBg: 'rgba(254,154,0,0.1)',
  },
  {
    icon: HelpCircle,
    label: 'Assistenza & FAQ',
    subtitle: "Supporto e guide all'uso",
    iconBg: 'rgba(0,188,125,0.1)',
  },
];

function SettingRow({ item }: { item: SettingItem }) {
  return (
    <div className="flex items-center justify-between px-4 py-0 h-[88px] rounded-2xl transition-colors hover:bg-white/[0.02] cursor-pointer group">
      <div className="flex items-center gap-5">
        {/* Icon */}
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
          style={{ backgroundColor: item.iconBg }}
        >
          <item.icon size={24} className="text-[#e2e8f0]" />
        </div>
        {/* Text */}
        <div>
          <p className="text-[#e2e8f0] font-bold text-lg leading-7">{item.label}</p>
          <p className="text-[#62748e] font-medium text-sm">{item.subtitle}</p>
        </div>
      </div>
      {/* Chevron */}
      <div className="w-10 h-10 bg-[#020618] border border-white/5 rounded-full flex items-center justify-center shrink-0 group-hover:border-white/10 transition-colors">
        <ChevronRight size={20} className="text-[#62748e]" />
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function LoyaltyProfileBlock(_: { block?: CmsBlock }) {
  const navigate = useNavigate();

  const handleLogout = () => {
    // In a real implementation this would call the logout API
    navigate('/login', { replace: true });
  };

  const initials = MOCK_USER.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="p-10 flex flex-col gap-8">
      {/* ── Page heading ─────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-white font-black text-[36px] leading-10 tracking-tight">
            Il Tuo Profilo
          </h1>
          <p className="text-[#90a1b9] text-base mt-1">
            Gestisci il tuo account, la privacy e le tue preferenze.
          </p>
        </div>
        {/* Action buttons */}
        <div className="flex items-center gap-3">
          <button className="w-12 h-12 bg-[#0f172b] border border-white/10 rounded-full flex items-center justify-center text-[#90a1b9] hover:text-white transition-colors shadow-lg">
            <Share2 size={20} />
          </button>
          <button className="w-12 h-12 bg-[#0f172b] border border-white/10 rounded-full flex items-center justify-center text-[#90a1b9] hover:text-white transition-colors shadow-lg">
            <Settings size={20} />
          </button>
        </div>
      </div>

      {/* ── Two-column content ────────────────────────────────────────────── */}
      <div className="flex gap-8">
        {/* Left column */}
        <div className="w-[297px] shrink-0 flex flex-col gap-8">
          {/* Profile card */}
          <div className="bg-[#0f172b] border border-white/5 rounded-[32px] overflow-hidden shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] relative">
            {/* Green gradient header */}
            <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-[rgba(0,79,59,0.4)] to-transparent pointer-events-none" />

            <div className="relative flex flex-col items-center pt-8 pb-8 px-8">
              {/* Avatar */}
              <div className="relative">
                <div className="w-32 h-32 rounded-full bg-[#1d293d] flex items-center justify-center border-[6px] border-[#0f172b] shadow-[0_0_0_4px_#00bc7d] text-white font-black text-3xl">
                  {initials}
                </div>
                {/* Level badge */}
                <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-[#00bc7d] border-4 border-[#0f172b] rounded-full px-3 py-1 flex items-center gap-1 shadow-[0_8px_10px_0_rgba(0,0,0,0.1)]">
                  <Flame size={10} className="text-[#020618]" />
                  <span className="text-[#020618] font-black text-xs leading-none whitespace-nowrap">
                    LV. {MOCK_USER.level}
                  </span>
                </div>
              </div>

              {/* Name & member info */}
              <div className="mt-8 text-center">
                <h2 className="text-white font-black text-3xl leading-9">{MOCK_USER.name}</h2>
                <div className="flex items-center justify-center gap-1.5 mt-3">
                  <Award size={20} className="text-[#00d492]" />
                  <p className="text-[#00d492] font-bold text-base">{MOCK_USER.memberType}</p>
                </div>
                <p className="text-[#62748e] font-medium text-sm mt-1">{MOCK_USER.memberId}</p>
              </div>
            </div>
          </div>

          {/* Stats cards row */}
          <div className="flex gap-4">
            {/* Punti Totali */}
            <div className="flex-1 bg-[#0f172b] border border-white/5 rounded-3xl px-6 py-6 shadow-lg">
              <p className="text-[#90a1b9] font-bold text-xs uppercase tracking-widest leading-4">
                Punti Totali
              </p>
              <p className="text-white font-black text-2xl mt-2">
                {MOCK_USER.points.toLocaleString('it-IT')}
              </p>
            </div>

            {/* Badge */}
            <div className="flex-1 bg-[#0f172b] border border-white/5 rounded-3xl px-6 py-6 shadow-lg">
              <p className="text-[#90a1b9] font-bold text-xs uppercase tracking-widest leading-4">
                Badge
              </p>
              <p className="font-black text-2xl mt-2">
                <span className="text-[#00d492]">{MOCK_USER.badgesUnlocked}</span>
                <span className="text-[#45556c] text-lg">/{MOCK_USER.badgesTotal}</span>
              </p>
            </div>
          </div>

          {/* Login Streak card */}
          <div className="bg-[#0f172b] border border-[rgba(255,105,0,0.2)] rounded-3xl px-6 py-5 flex items-center justify-between shadow-[0_10px_15px_0_rgba(126,42,12,0.1),0_4px_6px_0_rgba(126,42,12,0.1)]">
            <div>
              <p className="text-[#90a1b9] font-bold text-xs uppercase tracking-widest">
                Login Streak
              </p>
              <p className="text-white font-medium text-sm mt-1 max-w-[160px] leading-5">
                Mantieni la scia per bonus extra
              </p>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <span className="text-[#ff6900] font-black text-4xl leading-none">
                {MOCK_USER.streak}
              </span>
              <Flame size={32} className="text-[#ff6900]" />
            </div>
          </div>
        </div>

        {/* Right column: Account settings */}
        <div className="flex-1 bg-[#0f172b] border border-white/5 rounded-[32px] overflow-hidden shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)]">
          {/* Header */}
          <div className="px-6 py-6 border-b border-white/5 bg-[rgba(15,23,43,0.5)]">
            <h3 className="text-white font-bold text-xl leading-7">Impostazioni Account</h3>
          </div>

          {/* Settings list */}
          <div className="px-4 py-4 flex flex-col gap-2">
            {SETTINGS.map((item) => (
              <SettingRow key={item.label} item={item} />
            ))}
          </div>

          {/* Logout button */}
          <div className="px-8 py-8 mt-auto border-t border-white/5 bg-[rgba(2,6,24,0.5)]">
            <button
              onClick={handleLogout}
              className="w-full bg-[rgba(251,44,54,0.1)] border border-[rgba(251,44,54,0.2)] rounded-2xl h-[62px] flex items-center justify-center gap-2 text-[#fb2c36] font-bold text-sm uppercase tracking-widest hover:bg-[rgba(251,44,54,0.15)] transition-colors shadow-lg"
            >
              <LogOut size={20} />
              Esci dall&apos;Account
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
